import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"

import { useSignUpModalState } from "@fuse/sharedComponents/globalQuickSignUpModal"
import {
  showErrorToast,
  showInfoToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import {
  EVENT_CLASS,
  Question,
  TAnalyticsEventParams,
  TTicket,
  TTicketForAttendee,
  TWebUser,
} from "@letshang/constants"
import {
  filter,
  flatten,
  get,
  isEmpty,
  isEqual,
  keyBy,
  last,
  map,
  mapValues,
  omit,
  size,
  sumBy,
} from "lodash"
import { useRecoilState, useResetRecoilState } from "recoil"
import { ROUTES } from "src/App"
import { useSessionStorage } from "usehooks-ts"

import { allEventCacheState, eventDetailsState } from "@fuse/recoil/events"
import { userState } from "@fuse/recoil/signup"

import {
  ELogAnalyticEvent,
  logAnalyticEvent,
} from "@firebaseConfig/analyticsUtil"

import {
  useCreatePaymentSheet,
  useGetAllTickets,
} from "@api/services/events/useEventsApi"

import { WhichBtnState } from "@fuse/pages/eventPage/controller"
import useFetchEventDetails from "@fuse/pages/eventPage/useFetchEventDetails"
import { useBuyTicketsSession } from "@fuse/pages/eventSignUp/BuyTickets/buyTicketsSessionManager"
import { EBuyTicketSessionKeys } from "@fuse/pages/eventSignUp/BuyTickets/utils"

// type TAnalyticsEventParams = {
//   uid: string
//   page_name: string
//   event_id: string
//   event_name: string
//   event_type: EVENT_CLASS
//   amount?: number // Optional, only for StripeCheckout
// }

export const useChooseTicket = (invitedId?: string, pagename?: string) => {
  const params = useParams()
  const eventId = get(params, "eventId", "")
  const location = useLocation()
  const navigate = useNavigate()

  const [isBuyForGroup, setIsBuyForGroup] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [user, setUser] = useRecoilState(userState)

  const needSignUp = !user.isLoggedIn
  const { state, refresh: refreshEventData } = useFetchEventDetails(eventId)

  const eventLoading = get(state, "isLoading", false)
  const questionnaires = get(location, "state.attendeeQuestioners", [])
  const currentEventDetails = get(state, "eventDetails", {})
  const flyerImage = get(currentEventDetails, "eventDocData.flyerImage", "")
  const eventName = get(currentEventDetails, "eventDocData.eventName", "-")

  const baseQuestionnaires: Question[] = get(
    currentEventDetails,
    "eventDocData.questionnaires",
    [],
  )

  const { data: tickets, isLoading: allTicketLoader } =
    useGetAllTickets(eventId)
  const allTicketsList: TTicket[] = get(tickets, "data.tickets", [])

  const {
    setBuyTicketsSessionItem,
    getBuyTicketsSessionItem,
    handleAddQuantity,
    handleSubtractQuantity,
  } = useBuyTicketsSession(currentEventDetails)
  const { fastSignUpStage, setFastSignUpStage, setShowFastSignUpPopUp } =
    useSignUpModalState()
  const ticketItem = useRef<TTicket[] | null>(null)
  const isFreeTicket = useRef(false)

  const isQuestionnairesNeeded = size(baseQuestionnaires) > 0

  const eventsTickets = getBuyTicketsSessionItem(
    EBuyTicketSessionKeys.EVENTS_TICKETS,
  )
  const eventClass = get(
    currentEventDetails,
    "eventDocData.eventClass",
    EVENT_CLASS.NON_TICKETED_EVENT,
  ) as EVENT_CLASS
  const isTicketedEvent = eventClass === EVENT_CLASS.TICKETED_EVENT

  const [selectedDonateAmount, setSelectedDonateAmount] =
    useSessionStorage<number>(EBuyTicketSessionKeys.SELECTED_DONATE_AMOUNT, 0)

  const loggedInUserId = get(user, "uid", "")
  const donations = get(tickets, "data.donations", [])
  const eventDocData = get(currentEventDetails, "eventDocData", {})
  const isAdmin = get(eventDocData, "initiatorUid", null) === loggedInUserId

  useEffect(() => {
    if (!isEmpty(allTicketsList)) {
      const updateEventsTickets = map(allTicketsList, function (ticket) {
        return {
          ...ticket,
          quantity: 0,
        }
      })

      // setEventsTickets(updateEventsTickets)
      const areEqualWithoutQuantity = isEqual(
        map(eventsTickets, (ticket) => omit(ticket, ["quantity"])),
        map(updateEventsTickets, (ticket) => omit(ticket, ["quantity"])),
      )

      if (!areEqualWithoutQuantity) {
        // setEventsTickets(updateEventsTickets)
        setBuyTicketsSessionItem(
          EBuyTicketSessionKeys.EVENTS_TICKETS,
          updateEventsTickets,
        )
      }
    }
  }, [allTicketsList])

  const onLogAnalyticEvent = (): void | undefined => {
    const commonParams: TAnalyticsEventParams = {
      uid: loggedInUserId,
      page_name: pagename,
      event_id: eventId,
      event_name: get(eventDocData, "eventName", ""),
      event_type: isTicketedEvent
        ? EVENT_CLASS.TICKETED_EVENT
        : EVENT_CLASS.NON_TICKETED_EVENT,
    }

    logAnalyticEvent(ELogAnalyticEvent.CHOOSE_TICKET_WEB, commonParams)
  }

  useEffect(() => {
    if (!isEmpty(eventDocData) && !isAdmin) {
      onLogAnalyticEvent()
    }
  }, [eventDocData])

  useEffect(() => {
    if (fastSignUpStage === "done" && !isEmpty(ticketItem.current)) {
      handlePurchasingTickets({ isFreeTicket: isFreeTicket.current })

      setFastSignUpStage(null)
      isFreeTicket.current = false
      ticketItem.current = null
    }
  }, [fastSignUpStage])

  const handleSignupClick = () => {
    setUser((prevUser: TWebUser) => ({
      ...prevUser,
      redirectUrl: location.pathname,
      eventId: eventId,
    }))
    // navigate(ROUTES.SIGNUP);
    // setStep(1);
    setShowFastSignUpPopUp(true)
  }

  const onFillQuestioners = (tickets: TTicket[]) => {
    const item = last(tickets)
    if (isBuyForGroup) {
      navigate(ROUTES.FILL_QUESTIONNAIRE.replace(":eventId", eventId), {
        state: { invitedId: invitedId },
      })
    } else {
      navigate(ROUTES.FILL_QUESTIONNAIRE.replace(":eventId", eventId), {
        state: { ticketsToBuy: item, invitedId: invitedId },
      })
    }
  }

  const onCheckoutScreen = (tickets: TTicket[]) => {
    // const item = last(tickets)
    try {
      if (isBuyForGroup) {
        navigate(ROUTES.STRIPE_CHECKOUT.replace(":eventId", eventId), {
          state: { ticketsToBuy: tickets, invitedId: invitedId },
        })
      } else {
        navigate(ROUTES.STRIPE_CHECKOUT.replace(":eventId", eventId), {
          state: { ticketsToBuy: tickets, invitedId: invitedId },
        })
      }
    } catch (e) {
      console.error("Error navigating to checkout screen", e)
    }
  }

  const onSelectTicket = (item: TTicket) => {
    if (needSignUp) {
      handleSignupClick()
      ticketItem.current = [item]
    } else {
      if (isQuestionnairesNeeded) {
        onFillQuestioners([item])
      } else {
        onCheckoutScreen([item])
      }
    }
  }

  const filteredTickets = filter(
    eventsTickets,
    (ticket) => ticket.quantity >= 1,
  )

  const onFillFriendsInfoScreen = (filteredTickets: TTicket[]) => {
    try {
      navigate(ROUTES.FILL_FRIENDS_TICKET_INFO.replace(":eventId", eventId), {
        state: {
          invitedId: invitedId,
          groupTicketsInfo: filteredTickets,
        },
      })
    } catch (e) {
      console.error(
        "Error navigating to Fill Friends ticket info/questionnaire screen",
        e,
      )
    }
  }

  const {
    mutate,
    data: paymentSheetData,
    isLoading: paymentSheetLoading,
  } = useCreatePaymentSheet()

  const [cache, setCache] = useRecoilState(allEventCacheState)
  const reset = useResetRecoilState(eventDetailsState)

  const onBuyFreeTicket = (ticketsToBuy: TTicket[]) => {
    setIsLoading(true)
    const ticketsForAttendee: TTicketForAttendee[] = map(
      ticketsToBuy,
      function (obj) {
        const addOns = map(
          filter(get(obj, "ticketAddOns", []), ["isSelected", true]),
          function (obj) {
            return {
              ticketId: get(obj, "ticketId", ""),
            }
          },
        )

        return {
          ticketId: get(obj, "id", get(obj, "ticketId", "")),
          attendeeId: get(obj, "attendeeId", ""),
          amount: get(obj, "ticketPrice", 0),
          addOns: addOns,
          ...(!!invitedId && { invitedId: invitedId }),
        }
      },
    )
    return new Promise((resolve, reject) => {
      const ticketsAnswersToQuestions = mapValues(
        keyBy(ticketsToBuy, "attendeeId"),
        "attendeeQuestionersAnswer",
      )

      const payload = {
        eventId: eventId,
        questionnaires: ticketsAnswersToQuestions,
        tickets: ticketsForAttendee,
      }

      // Add coupon object only if both values are present

      mutate(payload, {
        onSuccess: (data) => {
          resolve(data)
          const eventDetails = currentEventDetails
          if (cache[eventId]) {
            setCache((prev) => ({
              ...prev,
              [eventId]: {
                ...prev[eventId], // preserve other fields like eventType, hasError
                eventDetails: {
                  ...prev[eventId]?.eventDetails, // preserve existing eventDetails
                  myJoinStatus: ticketsToBuy[0]?.requireApproval
                    ? WhichBtnState.UnderReview
                    : WhichBtnState.Accepted, // update only this field
                },
              },
            }))
          }
          reset()
          navigate(ROUTES.STRIPE_SUCCESS.replace(":eventId", eventId), {
            state: {
              eventDetails,
              requiredApproval: ticketsToBuy[0]?.requireApproval,
            },
          })
          // setTimeout(() => {
          //   refreshEventData()
          // }, 1200)
          setIsLoading(false)
        },
        onError: (err: any) => {
          // Handle error - you can perform any actions needed upon error here
          const errMsg = err.message.includes("following capabilities enabled")
            ? "The host needs to enable ticket purchases. Check back shortly!"
            : err.message
          showErrorToast(`Error: ${errMsg}`)
          reject(err)
          setIsLoading(false)
        },
      })
    })
  }

  const handlePurchasingTickets = ({
    isFreeTicket,
  }: {
    isFreeTicket?: boolean
  }) => {
    const isHaveAddOns = !isEmpty(flatten(map(filteredTickets, "ticketAddOns")))
    if (isQuestionnairesNeeded || isHaveAddOns) {
      onFillFriendsInfoScreen(filteredTickets)
    } else {
      const ticketsToBuy = map(filteredTickets, (ticket) => {
        return {
          ...ticket,
          attendeeId: loggedInUserId,
        }
      })

      if (isFreeTicket) {
        onBuyFreeTicket(ticketsToBuy)
      } else {
        onCheckoutScreen(ticketsToBuy)
      }
    }
  }

  const onSelectGroupTicket = (totalWithFees: number) => {
    if (needSignUp) {
      handleSignupClick()
      ticketItem.current = filteredTickets
      isFreeTicket.current = totalWithFees === 0
    } else {
      handlePurchasingTickets({ isFreeTicket: totalWithFees === 0 })
    }
  }

  const onSingleGroupTicket = (ticketId: string) => {
    if (isEmpty(filteredTickets)) {
      handleAddQuantity(ticketId)
      const validateFilteredTickets = map(
        filter(eventsTickets, { id: ticketId }),
        (ticket) => ({ ...ticket, quantity: 1 }),
      )
      if (needSignUp) {
        handleSignupClick()
        ticketItem.current = validateFilteredTickets
      } else {
        const isHaveAddOns = !isEmpty(
          flatten(map(validateFilteredTickets, "ticketAddOns")),
        )

        if (isQuestionnairesNeeded || isHaveAddOns) {
          onFillFriendsInfoScreen(validateFilteredTickets)
        } else {
          const ticketsToBuy = map(validateFilteredTickets, (ticket) => {
            return {
              ...ticket,
              attendeeId: loggedInUserId,
            }
          })
          onCheckoutScreen(ticketsToBuy)
        }
      }
    } else {
      showInfoToast("Tap the + or – to change the ticket quantity.")
    }
  }

  const ticketsWithTotalPrice = sumBy(
    filteredTickets,
    (ticket) => ticket?.quantity * ticket?.ticketPrice,
  )

  return {
    eventId,
    isLoading: isLoading || eventLoading || allTicketLoader,
    eventsTickets,
    isBuyForGroup,
    setIsBuyForGroup,
    onSelectTicket,
    onSelectGroupTicket,
    flyerImage,
    eventName,
    filteredTickets,
    selectedDonateAmount,
    setSelectedDonateAmount,
    donations,
    handleAddQuantity,
    handleSubtractQuantity,
    onSingleGroupTicket,
    ticketsWithTotalPrice,
  }
}
